// @ts-nocheck
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { DataTable } from '@/components/organism/Table';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import SegmentedControl from '@/components/elements/SegmentedControl';
import Autocomplete from '@/components/elements/AutoComplete';
import CommonDropdownSelector from '@/components/organism/GroupDropdown';
import DownloadReport from '@/components/organism/ExportDataModal/DownloadReport';
import { getSelectedTimeZone } from '@/utils/dateTime';
import {
  ALL_OPERATOR,
  ROW_PER_PAGE_OPTION,
  pagination as PAGINATION_INITIAL_STATE,
  STRINGS,
  DROPDOWN_OPTIONS,
  REPORT_FILE_NAMES,
  MODAL_TITLES,
  SortEnum,
} from '@/constant/global/global.constant';
import { getDevice } from '@/store/slices/device.slice';
import { getAboutDevice } from '@/store/selectors/device.selector';
import {
  EReportFormat,
  EReportLabel,
} from '@/constant/downloadReport/downloadReport.const';
import { API_URL_CONFIG } from '@/config/urlConfig/apiUrlConfig';
import { DEVICE_PROPS, EDeviceType } from '@/constant/device/device.constant';
import { EMarketsData } from '@/constant/market/market.constant';
import {
  EExportPage,
  EAllowedFieldsForExport,
} from '@/components/organism/ExportDataModal/export.types';
import { useExportFormats } from '@/hooks/useExportFormats';
import { useDateFilter } from '@/context/dateFilter.context';
import { useDebounce } from '@/hooks/useDebounce';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import GlobalSearch from '@/components/atoms/GlobalSearch';
import { ERequestMethods } from '@/constant/downloadReport/downloadReport';
import { DeviceExpandedPanel } from '@/components/organism/TableExpand/DeviceExpanded/DeviceExpandedPanel';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import { tid } from '@/utils/testId';
import { DEVICE_TEST_IDS as TID } from '@/constant/testIds/device.testIds';
import type { IDeviceProps } from './device.interface';
import { buildColumns } from './device.columns';

/* ── Page ───────────────────────────────────────────────────────────────── */
const Device = () => {
  const dispatch = useDispatch();
  const selectedTimezone = getSelectedTimeZone();
  const exportFormats = useExportFormats(EExportPage.DEVICE);
  const { filterValues } = useDateFilter();
  const hasLoaded = useRef(false);

  const [isCopy, setIsCopy] = useState('');
  const [activeStatus, setActiveStatus] = useState<string>('1');
  const [deviceType, setDeviceType] = useState(EDeviceType[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);
  const [sorting, setSorting] = useState({
    orderBy: 'updatedOn',
    order: SortEnum.desc,
  });
  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);
  const [selection, setSelection] = useState({
    operator: ALL_OPERATOR,
    market: DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION,
  });

  const {
    loading,
    devices,
    pagination: paginationResponse,
  } = useSelector(getAboutDevice);

  // Track first successful data load
  useEffect(() => {
    if (!hasLoaded.current && Array.isArray(devices) && devices.length > 0) {
      hasLoaded.current = true;
    }
  }, [devices]);

  const totalPages = Math.ceil(
    (paginationResponse?.totalRecords || 0) / pagination.limit,
  );

  const searchTermQuery = useDebounce(
    searchTerm.trim(),
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );

  const onCopyClick = useCallback((key: string) => {
    setIsCopy(key);
    setTimeout(() => setIsCopy(''), 500);
  }, []);

  const handleSelectionChange = useCallback(({ operator, market }: any) => {
    setSelection({ operator, market });
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setPagination((p) => ({
        ...p,
        limit: value,
        page: totalPages < p.page ? totalPages : p.page,
      }));
    },
    [totalPages],
  );

  const handlePreviousClick = useCallback((page: number) => {
    if (page > 1) setPagination((p) => ({ ...p, page: page - 1 }));
  }, []);

  const handleNextClick = useCallback(
    (page: number) => {
      if (page < totalPages) setPagination((p) => ({ ...p, page: page + 1 }));
    },
    [totalPages],
  );

  useEffect(() => {
    if (pagination?.page > totalPages && totalPages !== 0) {
      setPagination((p) => ({ ...p, page: 1 }));
    }
  }, [totalPages, pagination?.page]);

  useEffect(() => {
    if (searchTermQuery && searchTermQuery.length < 3) return;
    dispatch(
      getDevice({
        ...pagination,
        ...sorting,
        startDate: filterValues?.startDate,
        endDate: filterValues?.endDate,
        includeInactive: activeStatus === 'all' ? true : undefined,
        inactive: activeStatus === '0' ? true : undefined,
        type: deviceType?.queryParam,
        ...(selection?.operator?.id !== ALL_OPERATOR.id
          ? { operatorId: selection?.operator?.id }
          : {}),
        ...(selection?.market?.id !== EMarketsData[0]?.marketId
          ? { marketId: selection?.market?.id }
          : {}),
        marketId: selection?.market?.id,
        ...(searchTermQuery && searchTermQuery.length >= 3
          ? { deviceIdentifier: searchTermQuery }
          : {}),
      }),
    );
  }, [
    pagination.limit,
    pagination.page,
    sorting,
    filterValues?.startDate,
    filterValues?.endDate,
    activeStatus,
    deviceType,
    selection?.operator?.id,
    selection?.market?.id,
    searchTermQuery,
  ]);

  const columns = useMemo(
    () => buildColumns(isCopy, onCopyClick),
    [isCopy, onCopyClick],
  );
  const allowedFields = EAllowedFieldsForExport[EExportPage.DEVICE] || [];

  return (
    <FullPageCard
      testId={tid(TID.PAGE)}
      className="rounded-b-none"
      header={
        <PageToolbar
          title={STRINGS.DEVICE}
          subtitle={STRINGS.DEVICE_SUB}
          icon={getNavItemIcon(NAV_ITEMS.DEVICES)}
          actionsSlot={
            <GlobalSearch
              className="w-[220px]"
              placeholder={DEVICE_PROPS.SEARCH.PLACEHOLDER}
              dataTestId={tid(TID.SEARCH_INPUT)}
              globalSearch={searchTerm}
              setGlobalSearch={(v: string) => {
                setSearchTerm(v);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            />
          }
          actionButtons={
            <DownloadReport
              testId={tid(TID.EXPORT_BTN)}
              label={EReportLabel.EXPORT_DATA}
              type={EReportFormat.CSV}
              url={API_URL_CONFIG.DEVICE.GET_DOWNLOAD_PDF_CSV}
              orderBy={sorting?.orderBy}
              order={sorting?.order}
              marketId={selection?.market?.id}
              operatorId={selection?.operator?.id}
              includeInactive={activeStatus === 'all' ? true : undefined}
              inactive={activeStatus === '0' ? true : undefined}
              startDate={filterValues?.startDate}
              endDate={filterValues?.endDate}
              limit={paginationResponse?.totalRecords}
              timeZone={selectedTimezone}
              fileName={REPORT_FILE_NAMES.DEVICES_REPORT}
              modalTitle={MODAL_TITLES.DEVICE_PAGE}
              supportingReportFormats={exportFormats}
              deviceIdentifier={searchTermQuery}
              requestMethod={ERequestMethods.POST}
              deviceType={deviceType?.queryParam}
              selectiveFieldExport
              allowedFieldsForExport={allowedFields}
            />
          }
          filtersLeft={
            <CommonDropdownSelector
              onSelectionChange={handleSelectionChange}
              showProvider={false}
              showOperator
              showLocation={false}
              showMarket
              showDevice={false}
              dataTestId={tid(TID.FILTER_DROPDOWN)}
            />
          }
          filtersRight={
            <div className="flex items-center gap-2">
              <div className="w-[160px]">
                <Autocomplete
                  label={DEVICE_PROPS.DEVICE_TYPES.DEVICE_TYPE}
                  placeholder={DEVICE_PROPS.DEVICE_TYPES.SELECT_DEVICE_TYPE}
                  selectedValue={deviceType}
                  handleSelectedValue={(v: any) => {
                    setDeviceType(v);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  lists={EDeviceType}
                  isCapitalised
                  dataTestId={tid(TID.DEVICE_TYPE_SELECT)}
                />
              </div>
              <div className="h-6 w-px bg-gray-200 dark:bg-gray-700/60" />
              <SegmentedControl
                testId={tid(TID.STATUS_FILTER)}
                size="lg"
                shape="square"
                variant="gradient"
                options={[
                  { value: 'all', label: 'All' },
                  { value: '1', label: 'Active' },
                  { value: '0', label: 'Inactive' },
                ]}
                value={activeStatus}
                onChange={(v: string) => {
                  setActiveStatus(v);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
              />
            </div>
          }
        />
      }
    >
      <DataTable
        data-testid={tid(TID.TABLE)}
        data={devices ?? []}
        columns={columns}
        loading={loading}
        skeletonRows={15}
        onSort={(orderBy, order) =>
          setSorting({ orderBy, order: order as SortEnum })
        }
        sortState={sorting}
        rowKey={(item: any) => item?.sourceId ?? ''}
        rowTestId={(item: any, idx: number) =>
          TID.TABLE_ROW(`${idx}`)
        }
        pagination={
          !isEmpty(devices)
            ? {
              state: pagination,
              totalItem: paginationResponse?.totalRecords,
              rowsPerPageOptions: ROW_PER_PAGE_OPTION,
              onRowsPerPageChange: handlePageSizeChange,
              onNextClick: handleNextClick,
              onPreviousClick: handlePreviousClick,
            }
            : null
        }
        expandedRowIndex={expandedRowIndex}
        onExpandChange={(idx) =>
          setExpandedRowIndex(idx === expandedRowIndex ? null : idx)
        }
        renderExpandedRow={(row) => <DeviceExpandedPanel row={row} />}
      />
    </FullPageCard>
  );
};

export default Device;
